var _ = require('optimizely-client-fn');
var Logger = require('core/utils/logger');
var WindowEnv = require('../env/window');

/**
 * @param {number} [windowLength] The size of the moving time window (in ms) in which we'll compute the rate.
 * @constructor
 */
function RateMeterClass(windowLength?:number) {
  this.windowLength = windowLength;

  // The number of occurrences in the current time window.
  this.count = 0;

  /*
   * Maps (count threshold) to (lists of listeners).
   *
   * E.g. {
   *    5: [function() { ... }],
   *    10: [function() { ... }, function() { ... }],
   * }
   */
  this.listeners = {} as any;

  // Whether the count has already incremented in the current ~tick of the event loop.
  this.isIncrementingTick = false;
}

/**
 * Indicate that the count should increase by exactly 1 due to the current tick of the event loop,
 * no matter how many times countCurrentTick is called during the various stack frames (the macrotask,
 * all microtasks, etc.) that comprise the current tick.
 *
 * At least, that's the intent.
 *
 * More precisely, the count will increase by exactly 1 as a result of all countCurrentTick calls that occur before
 * whatever tick of the event loop we can claim with setTimeout(..., 0). This means that there could be multiple ticks
 * of the event loop (and therefore multiple macrotasks) that run before our setTimeout(..., 0), all of whose
 * countCurrentTick calls will combine to increase the count by only 1.
 *
 * Why do this?
 *
 * We use RateMeter to detect update to the customer's website. Whether the customer is using hand-rolled code or a
 * modern frontend, when they try to mutate elements in the DOM in a particular stack frame, our various
 * MutationObservers (the global observer for new elements, the element-specific reapply observers, etc.) are invoked in
 * distinct "compound microtask subtasks" (and in fact, these microtasks can spawn further microtasks if there are
 * codependent changes). A given change's RateMeter should measure a _single_ semantic page update no matter how many
 * microtasks the change's (re)applications are spread across.
 */
RateMeterClass.prototype.countCurrentTick = function() {
  if (this.isIncrementingTick) {
    return;
  }
  // Prevent other callers from incrementing the rate again later in this tick.
  this.isIncrementingTick = true;

  // Synchronously increment the count and alert listeners before we return control to the caller.
  this.increment();

  WindowEnv.setTimeout(_.bind(function endIncrementingTick() {
    this.isIncrementingTick = false;
  }, this), 0);
};

/**
 * Increase the count by 1.
 *
 * Automatically decrements by 1 once the incrementing event has slipped out of the applicable time window.
 *
 * @private
 */
RateMeterClass.prototype.increment = function() {
  this.count += 1;

  _.forEach(this.listeners[String(this.count)], function(listenerAtThreshold) {
    listenerAtThreshold();
  });

  WindowEnv.setTimeout(_.bind(this.decrement, this), this.windowLength);
};

/**
 * Decrease the count by 1.
 *
 * @private
 */
RateMeterClass.prototype.decrement = function() {
  this.count -= 1;

  if (this.count < 0) {
    Logger.warn('Decremented down to negative count: ', this.count);
    this.count = 0;
  }
};

/**
 * @param {number} [threshold] The threshold at which the listener should be invoked.
 * @param {function} [listener] A function that will be called every time the count begins to meet or exceed the
 *                              threshold.
 */
RateMeterClass.prototype.addListener = function(threshold?:number, listener?:Function) {
  if (!this.listeners[threshold]) {
    this.listeners[threshold] = [];
  }
  this.listeners[threshold].push(listener);
};

module.exports = RateMeterClass;
